import { useEffect, useMemo, useState } from "react";
import { getPages } from "./ui";
import { apiGet } from "./http";
export interface Sorting<T extends string = string> {
  by: T;
  order: "asc" | "desc";
}

export const useSorting = <T extends string = string>(
  by: T,
  order: Sorting["order"] = "asc"
): [Sorting<T>, (by: T) => void] => {
  const [sorting, setSorting] = useState<Sorting<T>>({ by, order });

  return useMemo(
    () => [
      sorting,
      (by: T) => {
        let order: any = "asc";
        if (sorting.by === by) {
          order = sorting.order === "asc" ? "desc" : "asc";
        }
        setSorting({ by, order });
      },
    ],
    [sorting]
  );
};

export interface TableEntry {
  id: number;
  title: string;
  date: string;
  is_template: boolean;
  modified: string;
}

export interface TablesApiResponse {
  posts: TableEntry[];
  current_page: number;
  per_page: number;
  total_pages: number;
  total_count: number;
}
export interface TablesPaginationInfo {
  loading: boolean;
  posts: {
    id: number;
    title: string;
    date: string;
    modified: string;
    is_template: boolean;
  }[];
  current_page: number;
  page_start: number | string;
  page_end: number | string;
  total_count: number | string;
  pages: number[];
  total_pages: number;
}

const defaultPage: TablesPaginationInfo = {
  loading: true,
  posts: [],
  current_page: 1,
  page_start: "...",
  page_end: "...",
  total_count: "...",
  pages: [],
  total_pages: 1,
};

type UseTableReturn = [
  TablesPaginationInfo,
  (tables: TablesPaginationInfo["posts"]) => void,
  () => void,
];

interface UseTableProps {
  page: number;
  search: string | undefined;
  status?: string;
  skipTables?: string;
  sort?: Sorting;
  per_page?: number;
  abortCtrl?: AbortController;
  refetchOnSkipChange?: boolean;
}

export const useTablePage = ({
  page,
  search,
  status,
  skipTables,
  sort,
  per_page,
  abortCtrl,
  refetchOnSkipChange,
}: UseTableProps): UseTableReturn => {
  const [apiData, setApiData] = useState<TablesPaginationInfo>(defaultPage);
  const [re, setRe] = useState(false);

  const setPageTables = (
    tables: TablesPaginationInfo["posts"],
    doSort?: boolean
  ) => {
    if (doSort) {
      let sortField: keyof TableEntry;
      if (!["id", "title", "date", "modified"].includes(sort.by || "id")) {
        sortField = "id";
      } else {
        sortField = sort.by as keyof TableEntry;
      }

      if (sortField !== "id") {
        if (sort.order === "asc") {
          tables.sort((a: TableEntry, b: TableEntry) => {
            // @ts-expect-error is_template is a boolean
            return a[sortField].localeCompare(b[sortField]);
          });
        } else {
          tables.sort((a: TableEntry, b: TableEntry) => {
            // @ts-expect-error is_template is a boolean
            return b[sortField].localeCompare(a[sortField]);
          });
        }
      } else {
        if (sort.order === "asc") {
          tables.sort((a: TableEntry, b: TableEntry) => {
            return a[sortField] - b[sortField];
          });
        } else {
          tables.sort((a: TableEntry, b: TableEntry) => {
            return b[sortField] - a[sortField];
          });
        }
      }
    }

    setApiData({
      ...apiData,
      posts: tables,
    });
  };

  const refetchForSkipChange = refetchOnSkipChange ? skipTables : "";

  useEffect(() => {
    const params = new URLSearchParams({
      page: page.toString(),
      search: search ? search : "",
      status: status ? status : "",
      except: skipTables ? skipTables : "",
      ...(sort ? { sort_by: sort.by, sort_order: sort.order } : {}),
      ...(per_page ? { per_page: per_page.toString() } : {}),
    });
    const url = `/tables?${params.toString()}`;
    setApiData((apiData) => ({
      ...apiData,
      current_page: page,
      loading: true,
    }));
    apiGet<TablesApiResponse>(url, {
      signal: abortCtrl?.signal,
    })
      .then((data) => {
        const { posts, current_page, total_pages, total_count } = data;
        const pageStart = (current_page - 1) * data.per_page + 1;
        const pageEnd = pageStart + posts.length - 1;
        setApiData({
          loading: false,
          posts,
          current_page,
          page_start: pageStart,
          page_end: pageEnd,
          pages: getPages(total_pages, 2),
          total_pages,
          total_count,
        });
      })
      .catch(() => {
        setApiData({
          ...apiData,
          loading: false,
        });
        WPTB.utils.notify(
          "Something went wrong! Please reload the page!",
          "error",
          0
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, status, sort.by, sort.order, per_page, refetchForSkipChange, re]);

  return [apiData, setPageTables, () => setRe(!re)] as const;
};
